<template>
  <settings-modal
    class="forex-currency-limits-editor-modal t-forex-currency-limits-editor-modal"
  >
    <span
      slot="heading"
      class="font-weight-bold"
    >{{ title }}</span>

    <v-layout
      row
      wrap
    >
      <v-flex xs12>
        <v-autocomplete
          v-if="!hasCurrencyLimits"
          v-model="form.currency"
          class="pt-0"
          label="Currency"
          :items="currencies"
          :disabled="currencyLimitsApiRequest.loading"
          :loading="currencyLimitsApiRequest.loading"
          :error-messages="getValidationErrorMessage('currency')"
          @input="onCurrencyInput"
        />

        <template v-if="isCurrencySelected">
          <v-switch
            v-model="form.paymentsEnabled"
            class="pt-0 mt-0"
            color="primary"
            :disabled="hasProfileId"
            :label="form.paymentsEnabled ? 'Payments in GB available' : 'Payments in GB unavailable'"
            hide-details
          />

          <v-switch
            v-model="form.usPaymentsEnabled"
            class="pt-0"
            color="primary"
            :disabled="hasProfileId"
            :label="form.usPaymentsEnabled ? 'Payments in US available' : 'Payments in US unavailable'"
            hide-details
          />
        </template>
      </v-flex>
    </v-layout>

    <template v-if="isCurrencySelected">
      <data-heading
        heading="Day"
        spaced
      />

      <v-layout row>
        <v-flex xs4>
          <v-text-field
            v-model="form.dayOperationLimit"
            type="number"
            label="Operational limit"
            :error-messages="getValidationErrorMessage('dayOperationLimit')"
          />
        </v-flex>

        <v-flex xs4>
          <v-text-field
            v-model="form.dayPeriodLimit"
            type="number"
            label="Period limit"
            :error-messages="getValidationErrorMessage('dayPeriodLimit')"
          />
        </v-flex>

        <v-flex xs4>
          <v-text-field
            v-model="form.dayBalanceLimit"
            type="number"
            label="Balance limit"
            :error-messages="getValidationErrorMessage('dayBalanceLimit')"
          />
        </v-flex>
      </v-layout>

      <data-heading
        heading="Night"
        spaced
      />

      <v-layout row>
        <v-flex xs4>
          <v-text-field
            v-model="form.nightOperationLimit"
            type="number"
            label="Operational limit"
            :error-messages="getValidationErrorMessage('nightOperationLimit')"
          />
        </v-flex>

        <v-flex xs4>
          <v-text-field
            v-model="form.nightPeriodLimit"
            type="number"
            label="Period limit"
            :error-messages="getValidationErrorMessage('nightPeriodLimit')"
          />
        </v-flex>

        <v-flex xs4>
          <v-text-field
            v-model="form.nightBalanceLimit"
            type="number"
            label="Balance limit"
            :error-messages="getValidationErrorMessage('nightBalanceLimit')"
          />
        </v-flex>
      </v-layout>

      <data-heading
        heading="Weekends"
        spaced
      />

      <v-layout row>
        <v-flex xs4>
          <v-text-field
            v-model="form.weekendOperationLimit"
            type="number"
            label="Operational limit"
            :error-messages="getValidationErrorMessage('weekendOperationLimit')"
          />
        </v-flex>

        <v-flex xs4>
          <v-text-field
            v-model="form.weekendPeriodLimit"
            type="number"
            label="Period limit"
            :error-messages="getValidationErrorMessage('weekendPeriodLimit')"
          />
        </v-flex>

        <v-flex xs4>
          <v-text-field
            v-model="form.weekendBalanceLimit"
            type="number"
            label="Balance limit"
            :error-messages="getValidationErrorMessage('weekendBalanceLimit')"
          />
        </v-flex>
      </v-layout>

      <error-alert :error="error" />

      <template slot="actions">
        <v-btn
          class="t-confirm-button"
          color="primary"
          medium
          flat
          :disabled="loading"
          @click="onConfirmButtonClick"
        >{{ confirmButtonText }}</v-btn>

        <v-btn
          class="t-cancel-button"
          color="primary"
          medium
          flat
          @click="onCloseButtonClick"
        >cancel</v-btn>

        <template v-if="hasProfileId && hasCurrencyLimits">
          <v-spacer />

          <v-btn
            class="t-delete-button"
            color="error"
            medium
            flat
            @click="onDeleteButtonClick"
          >delete limits</v-btn>
        </template>
      </template>
    </template>
  </settings-modal>
</template>

<script>
  // Events
  import { EVENT_FOREX_CURRENCY_LIMITS_CHANGE } from '@/events/forexCurrencyLimits'

  // Models
  import ForexCurrencyLimits from '@/models/ForexCurrencyLimits'

  // Services
  import ApiRequest from '@/services/ApiRequests/ApiRequest'
  import PageableApiRequest from '@/services/ApiRequests/PageableApiRequest'
  import ForexCurrencyLimitsApi from '@/services/Api/ForexCurrencyLimitsApi'
  import ProfileApi from '@/services/Api/ProfileApi'

  // Mixins
  import form from '@/mixins/form'
  import modal from '@/mixins/modal'
  import flashMessages from '@/mixins/flashMessages'

  // Components
  import SettingsModal from '@/components/modals/SettingsModal'
  import ErrorAlert from '@/components/ui/ErrorAlert'
  import DataHeading from '@/components/ui/Data/DataHeading'

  export default {
    name: 'ForexCurrencyLimitsEditorModal',
    components: {
      SettingsModal,
      ErrorAlert,
      DataHeading
    },
    mixins: [form, modal, flashMessages],
    data () {
      return {
        form: {
          paymentsEnabled: false,
          usPaymentsEnabled: false,
          currency: null,
          dayOperationLimit: null,
          dayPeriodLimit: null,
          dayBalanceLimit: null,
          nightOperationLimit: null,
          nightPeriodLimit: null,
          nightBalanceLimit: null,
          weekendOperationLimit: null,
          weekendPeriodLimit: null,
          weekendBalanceLimit: null
        },
        saveCurrencyLimitsApiRequest: null,
        currencyLimitsApiRequest: new PageableApiRequest(ForexCurrencyLimitsApi.fetchAll, ForexCurrencyLimits)

      }
    },
    computed: {
      isCurrencySelected () {
        return this.parsedFormInput.currency != null
      },
      hasCurrencyLimits () {
        return this.modalData.currencyLimits != null
      },
      hasProfileId () {
        return this.modalData.profileId != null
      },
      requestArguments () {
        const requestArguments = [this.parsedFormInput]

        if (this.hasProfileId) {
          requestArguments.unshift(this.modalData.profileId)
        }

        return requestArguments
      },
      title () {
        return `${this.hasCurrencyLimits ? `Edit ${this.modalData.currencyLimits.currency}` : 'Add'} limits`
      },
      confirmButtonText () {
        return this.hasCurrencyLimits ? 'save changes' : 'add limits'
      },
      error () {
        if (this.saveCurrencyLimitsApiRequest == null) {
          return null
        }

        return this.saveCurrencyLimitsApiRequest.error
      },
      loading () {
        if (this.saveCurrencyLimitsApiRequest == null) {
          return false
        }

        return this.saveCurrencyLimitsApiRequest.loading
      },
      currencies () {
        return this.currencyLimitsApiRequest.data.map(({ currency }) => currency)
      }
    },
    created () {
      if (this.hasCurrencyLimits) {
        this.setFormData(this.modalData.currencyLimits)
      }

      this.setSaveCurrencyLimitsApiRequest(
        new ApiRequest(
          this.hasProfileId
            ? ProfileApi.saveForexCurrencyLimits
            : ForexCurrencyLimitsApi.save
        )
      )

      if (this.hasProfileId && !this.hasCurrencyLimits) {
        this.currencyLimitsApiRequest.execute({}).catch(() => {})
      }
    },
    methods: {
      onCurrencyInput (currency) {
        const currencyLimit = this.currencyLimitsApiRequest.data.find((currencyLimits) => currencyLimits.currency === currency)

        this.setFieldValue('paymentsEnabled', currencyLimit.paymentsEnabled)
        this.setFieldValue('usPaymentsEnabled', currencyLimit.usPaymentsEnabled)
      },
      onConfirmButtonClick () {
        this.saveCurrencyLimitsApiRequest
          .execute(...this.requestArguments)
          .then(() => {
            this.$eventHub.$emit(EVENT_FOREX_CURRENCY_LIMITS_CHANGE)

            this.showSuccessFlashMessage(
              this.hasCurrencyLimits
                ? 'Limits have been successfully edited.'
                : 'Limits have been successfully added.'
            )
          })
          .catch(() => {})
      },
      onDeleteButtonClick () {
        const { currencyLimits, profileId } = this.modalData

        setTimeout(() => {
          this.openModal(
            'ProfileForexCurrencyLimitsDeleteModal',
            { currencyLimits, profileId },
            { size: '500px' }
          )
        }, 100)

        this.closeModal()
      },
      onCloseButtonClick () {
        this.closeModal()
      },
      setSaveCurrencyLimitsApiRequest (saveCurrencyLimitsApiRequest) {
        this.$set(this, 'saveCurrencyLimitsApiRequest', saveCurrencyLimitsApiRequest)
      }
    }
  }
</script>
